/*
 * One membership in the cart, ever.
 *
 * Two memberships in one order means two subscription contracts and two
 * recurring charges, and nothing downstream unpicks that: the order webhook
 * creates a contract per subscription line without asking whether the customer
 * already has one. The pages that sell memberships now refuse to sell a second
 * one to somebody who has one, but that is presentation. This is the cart.
 *
 * Every add in this theme goes through Alpine.store("cart").addToCart, so that
 * is the one place worth standing. When the thing being added is a membership,
 * any membership already in the cart is removed first: choosing Patron after
 * choosing Collector replaces it rather than stacking, which is what somebody
 * picking a plan expects anyway.
 *
 * It cannot stop a hand-built POST to /cart/add. That is what the checkout
 * validation function is for; this is the part that makes the ordinary journey
 * impossible to get wrong.
 */
(function () {
  "use strict";

  /* The six membership products. Handles rather than variant ids, because a
     cart line reports its handle and an id would rot the day a product is
     recreated. */
  var MEMBERSHIP_HANDLES = [
    "member-monthly",
    "member-annual",
    "collector-monthly",
    "collector-annual",
    "patron-monthly",
    "patron-annual",
  ];

  var VARIANT_CACHE_KEY = "od-membership-variants-v1";
  var variantIds = null; // null = not resolved, array of ids once it is

  /* The last add that went through the cart store, so a form submit can tell
     whether the page's own script has already dealt with it. */
  var lastAdd = null;

  function root() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || "/";
  }

  function readCachedVariants() {
    try {
      var raw = sessionStorage.getItem(VARIANT_CACHE_KEY);
      if (!raw) return null;
      var parsed = JSON.parse(raw);
      if (!parsed || !Array.isArray(parsed.ids) || parsed.handles !== MEMBERSHIP_HANDLES.join(",")) {
        return null;
      }
      return parsed.ids;
    } catch (error) {
      return null;
    }
  }

  /*
   * Which variants are memberships. Resolved from the products themselves so
   * the list survives a product being rebuilt, and cached for the tab because
   * it cannot change while somebody is shopping.
   */
  function resolveVariantIds() {
    if (variantIds) return Promise.resolve(variantIds);

    var cached = readCachedVariants();
    if (cached) {
      variantIds = cached;
      return Promise.resolve(variantIds);
    }

    return Promise.all(
      MEMBERSHIP_HANDLES.map(function (handle) {
        return fetch(root() + "products/" + handle + ".js", {
          credentials: "same-origin",
          headers: { Accept: "application/json" },
        })
          .then(function (response) { return response.ok ? response.json() : null; })
          .then(function (product) {
            if (!product || !product.variants) return [];
            return product.variants.map(function (variant) { return Number(variant.id); });
          })
          .catch(function () { return []; });
      })
    ).then(function (lists) {
      var ids = [];
      for (var i = 0; i < lists.length; i++) {
        for (var j = 0; j < lists[i].length; j++) ids.push(lists[i][j]);
      }

      /* Nothing resolved means every request failed. Caching that would teach
         the tab there are no memberships at all. */
      if (!ids.length) return null;

      variantIds = ids;
      try {
        sessionStorage.setItem(
          VARIANT_CACHE_KEY,
          JSON.stringify({ handles: MEMBERSHIP_HANDLES.join(","), ids: ids })
        );
      } catch (error) {
        /* private mode: it just resolves again next time */
      }
      return ids;
    });
  }

  function isMembershipLine(item) {
    return MEMBERSHIP_HANDLES.indexOf(String(item && item.handle)) !== -1;
  }

  /* Removes every membership line in the cart, one at a time: /cart/change.js
     returns the whole cart each time, and sending them together is a race over
     the same cart. */
  function clearMemberships() {
    return fetch(root() + "cart.js", {
      credentials: "same-origin",
      headers: { Accept: "application/json" },
    })
      .then(function (response) { return response.json(); })
      .then(function (cart) {
        var keys = (cart && cart.items ? cart.items : [])
          .filter(isMembershipLine)
          .map(function (item) { return item.key; });

        return keys.reduce(function (chain, key) {
          return chain.then(function () {
            return fetch(root() + "cart/change.js", {
              method: "POST",
              credentials: "same-origin",
              headers: { "Content-Type": "application/json" },
              body: JSON.stringify({ id: key, quantity: 0 }),
            });
          });
        }, Promise.resolve());
      });
  }

  function wrap(store) {
    if (!store || typeof store.addToCart !== "function" || store.__odMembershipGuarded) {
      return Boolean(store && store.__odMembershipGuarded);
    }

    var original = store.addToCart;
    store.__odMembershipGuarded = true;

    store.addToCart = function (itemProps) {
      var self = this;
      var id = Number(itemProps && itemProps.id);

      lastAdd = { id: id, at: Date.now() };

      function add() { return original.call(self, itemProps); }

      if (!isFinite(id) || id <= 0) return add();

      return resolveVariantIds().then(
        function (ids) {
          /* A subscription line is the fallback test: if the product lookup
             failed we would rather treat a membership as a membership than let
             a second contract through. Memberships are the only selling plans
             in the shop. */
          var isMembership = ids
            ? ids.indexOf(id) !== -1
            : Boolean(itemProps && itemProps.selling_plan);

          if (!isMembership) return add();

          /* Never let a failed clean-up block the add: a cart with two
             memberships is stopped at checkout, an add that silently does
             nothing looks like a broken button. */
          return clearMemberships().then(add, add);
        },
        function () { return add(); }
      );
    };

    return true;
  }

  function attach() {
    /* Eagerly, because the form listener has to decide synchronously whether a
       variant is a membership, and a customer who clicks before it resolves
       gets the old behaviour. */
    resolveVariantIds().catch(function () {});

    var tries = 0;

    (function attempt() {
      tries += 1;
      var store = window.Alpine && typeof window.Alpine.store === "function"
        ? window.Alpine.store("cart")
        : null;

      if (wrap(store)) return;
      if (tries >= 100) return;
      window.setTimeout(attempt, 100);
    })();
  }

  /*
   * The form path, for when nothing else is listening.
   *
   * The wrapper above only covers adds that go through the cart store. A form
   * that posts to /cart/add natively never touches it, and that is not
   * hypothetical: it is exactly what these forms fall back to when a section's
   * script has not run. The customer then lands on Shopify's own "Something
   * went wrong" page — which cannot be styled, worded or replaced — reading a
   * validation error as though the shop had broken.
   *
   * So the native post is stopped, and then it waits a tick. If the page's own
   * script is alive it will have added through the store, which is guarded and
   * keeps its button states; if nothing happened, this does it instead.
   */
  function interceptFormPost(event) {
    var form = event.target;
    if (!form || form.tagName !== "FORM") return;

    var action = form.getAttribute("action") || "";
    if (action.indexOf("/cart/add") === -1) return;

    /*
     * A form the page is handling itself is not this listener's business.
     *
     * Without this the guard stopped the native post, waited a tick to see
     * whether anything had added through the cart store, and added it when
     * nothing had. On the Patron page nothing had *by design*: the upgrade
     * opens a confirmation and calls change-tier, no cart involved. So the
     * dialog appeared and the guard put Patron in the cart behind it, turning
     * an upgrade into a second subscription — the exact thing all of this
     * exists to prevent.
     */
    if (form.dataset.odCartBound === "1") return;

    // Not resolved yet: leave the page to behave as it always has rather than
    // swallow a submit over a list we do not have.
    if (!variantIds) return;

    var data = new FormData(form);
    var id = Number(data.get("id"));
    if (!isFinite(id) || variantIds.indexOf(id) === -1) return;

    event.preventDefault();

    /*
     * Nobody who already holds a membership adds another one to a cart.
     *
     * Only on this fallback path, never on the pages' own forms: a cancelled
     * member is still 'active' here until their paid period ends, and they are
     * allowed to start again — overlapping if they choose. Those buys go
     * through a claimed form and never reach this line.
     */
    var held = String(window.ODMembershipTier || "").toLowerCase();

    if (held) {
      var store = window.Alpine && typeof window.Alpine.store === "function"
        ? window.Alpine.store("cart")
        : null;

      if (store && store.error) {
        store.error.message =
          "You already have a membership. You can change your tier on your membership page.";
      }

      return;
    }

    window.setTimeout(function () {
      if (lastAdd && lastAdd.id === id && Date.now() - lastAdd.at < 1500) return;

      var props = {};
      data.forEach(function (value, key) { props[key] = value; });

      var store = window.Alpine && typeof window.Alpine.store === "function"
        ? window.Alpine.store("cart")
        : null;

      if (store && typeof store.addToCart === "function") {
        store.addToCart(props);
        return;
      }

      /* No Alpine at all. Clear first, then let the form go the way it meant
         to: a page load is the old behaviour, and better than the error page. */
      function post() { form.submit(); }
      clearMemberships().then(post, post);
    }, 0);
  }

  document.addEventListener("submit", interceptFormPost, true);

  window.ODMembershipCartGuard = {
    clearMemberships: clearMemberships,
    handles: MEMBERSHIP_HANDLES,
  };

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", attach);
  } else {
    attach();
  }
})();
